/**
 * Riferimenti di tempo del verbale verso l'audio (piano 2 dei verbali, Task 14): funzioni pure.
 *
 * Il verbale generato dalla trascrizione porta `riferimenti` `{ chiave, impronta, audioId, daMs, aMs }`:
 * un paragrafo della sezione `chiave` con quell'impronta viene dall'audio `audioId` a partire da
 * `daMs`. `improntaParagrafo` è speculare a `backend/utils/verbaliRiferimenti.js` (stesse uscite,
 * casi condivisi in shared/fixtures/verbaliRiferimenti.casi.json): le si passa l'innerHTML del
 * paragrafo (toglie i tag, `<br>` vale uno spazio, decodifica le entità).
 */

export interface RiferimentoVerbale {
  chiave: string;
  impronta: string;
  audioId: string | null;
  daMs: number;
  aMs?: number | null;
}

const LUNGHEZZA_IMPRONTA = 80;
/** Paragrafo ritoccato: basta che l'impronta del riferimento inizi con le sue prime 30 lettere. */
const LUNGHEZZA_PREFISSO = 30;

const ENTITA: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  agrave: 'à', aacute: 'á', egrave: 'è', eacute: 'é', igrave: 'ì', iacute: 'í',
  ograve: 'ò', oacute: 'ó', ugrave: 'ù', uacute: 'ú',
  Agrave: 'À', Egrave: 'È', Eacute: 'É', Igrave: 'Ì', Ograve: 'Ò', Ugrave: 'Ù',
};

/** Entità HTML ⇒ caratteri; un'entità con nome sconosciuta diventa uno spazio (come il backend). */
function decodificaEntita(testo: string): string {
  return testo.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (_intera: string, nome: string) => {
    if (nome.charAt(0) === '#') {
      const esadecimale = nome.charAt(1) === 'x' || nome.charAt(1) === 'X';
      const codice = parseInt(nome.slice(esadecimale ? 2 : 1), esadecimale ? 16 : 10);
      return Number.isFinite(codice) && codice > 0 && codice <= 0x10ffff ? String.fromCodePoint(codice) : ' ';
    }
    return Object.prototype.hasOwnProperty.call(ENTITA, nome) ? ENTITA[nome] : ' ';
  });
}

/**
 * Impronta di un paragrafo: senza tag (`<br>` = spazio), entità decodificate, minuscole, senza
 * accenti, solo lettere e cifre separate da uno spazio, i primi 80 caratteri (senza spazio finale).
 */
export function improntaParagrafo(testo: string): string {
  const senzaTag = String(testo == null ? '' : testo)
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '');
  return decodificaEntita(senzaTag)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .slice(0, LUNGHEZZA_IMPRONTA)
    .trim();
}

/**
 * Riferimento del paragrafo `testoParagrafo` (innerHTML) della sezione `chiave`: prima quello con la
 * stessa impronta, poi (paragrafo ritoccato) il primo della stessa sezione la cui impronta inizia con
 * le prime 30 lettere di quella del paragrafo; altrimenti null.
 */
export function trovaRiferimento(
  riferimenti: RiferimentoVerbale[],
  chiave: string,
  testoParagrafo: string
): { audioId: string; daMs: number } | null {
  if (!Array.isArray(riferimenti) || !riferimenti.length) return null;
  const impronta = improntaParagrafo(testoParagrafo);
  if (!impronta) return null;
  const validi = riferimenti.filter((r) => r && r.chiave === chiave && r.audioId && Number.isFinite(r.daMs));
  const esito = (r: RiferimentoVerbale) => ({ audioId: String(r.audioId), daMs: r.daMs });
  const uguale = validi.find((r) => r.impronta === impronta);
  if (uguale) return esito(uguale);
  if (impronta.length < LUNGHEZZA_PREFISSO) return null;
  const prefisso = impronta.slice(0, LUNGHEZZA_PREFISSO);
  const simile = validi.find((r) => typeof r.impronta === 'string' && r.impronta.startsWith(prefisso));
  return simile ? esito(simile) : null;
}

/** `riferimenti` di `GET /verbale` o di `fine` della generazione ⇒ elenco tipato (scarta il resto). */
export function riferimentiDa(v: unknown): RiferimentoVerbale[] {
  return (Array.isArray(v) ? v : [])
    .filter((r: any) => r && typeof r.chiave === 'string' && typeof r.impronta === 'string' && Number.isFinite(r.daMs))
    .map((r: any) => ({
      chiave: r.chiave,
      impronta: r.impronta,
      audioId: r.audioId ? String(r.audioId) : null,
      daMs: r.daMs,
      aMs: Number.isFinite(r.aMs) ? r.aMs : null,
    }));
}

/** Blocchi a cui il backend lega un riferimento (`p`, `li`, `h3`). */
const SELETTORE_BLOCCO = 'p, li, h3';

/**
 * Paragrafo sotto il cursore dentro `radice` e chiave della sua sezione (quella dell'`<h2>`
 * precedente; nessun `<h2>` prima ⇒ `preambolo`). `chiavi` sono le chiavi delle sezioni con titolo,
 * nell'ordine degli `<h2>` (da `dividiInSezioni`, senza il preambolo). null se il cursore è fuori.
 */
export function paragrafoAlCursore(
  radice: HTMLElement | null,
  nodo: Node | null,
  chiavi: string[]
): { chiave: string; html: string } | null {
  if (!radice || !nodo || !radice.contains(nodo)) return null;
  const elemento = (nodo.nodeType === 1 ? nodo : nodo.parentNode) as Element | null;
  const blocco = elemento && typeof elemento.closest === 'function' ? elemento.closest(SELETTORE_BLOCCO) : null;
  if (!blocco || !radice.contains(blocco)) return null;
  const titoli = Array.from(radice.querySelectorAll('h2'));
  let indice = -1;
  titoli.forEach((h, i) => {
    // eslint-disable-next-line no-bitwise
    if (h.compareDocumentPosition(blocco) & Node.DOCUMENT_POSITION_FOLLOWING) indice = i;
  });
  const chiave = indice < 0 ? 'preambolo' : chiavi[indice];
  if (!chiave) return null;
  return { chiave, html: blocco.innerHTML };
}
